import { ClientDataType } from "@/api/ClientApi";
import { ZoneDataType } from "@/api/ZoneApi";
import { importClients } from "@/api/ClientImportApi";
import { CLIENT_IMPORT_FIELDS, REQUIRED_IMPORT_FIELDS, findClientImportField } from "../clientImportFields";
import { ColumnMapping as ClientColumnMapping } from "../clientImportMapping";
import { buildImportPayload } from "../importPayload";
import { createMissingZones, indexZonesByName, loadExistingClientData } from "../loadExistingClientData";
import { applyCreatedZones, resolveImportRows } from "../resolveImportRows";
import { suggestClientColumnMapping } from "../suggestColumnMapping";
import { ImportDefinition } from "../core/types";

export type ClientImportContext = {
    clients: ClientDataType[];
    zones: ZoneDataType[];
};

/** Todo lo que el asistente de importacion necesita saber del modelo `Client`. */
export const clientImportDefinition: ImportDefinition<ClientDataType, ClientImportContext> = {
    title: "Importar Clientes",
    backUrl: "/user/clients",
    doneActionTitle: "Ir a la lista de clientes",
    entityPlural: "clientes",
    fileHelpText:
        "Seleccione el archivo de clientes exportado de su sistema anterior. Se aceptan archivos CSV y Excel, con o sin fila de encabezados.",
    mappingHelpText:
        "Indique a que dato del cliente corresponde cada columna. Las columnas que su sistema use y Easypos no, puede dejarlas sin asignar.",
    duplicateQuestion: "Si el cliente ya existe (por codigo o identificacion):",
    errorRowsHelp: (errorCount, totalRows) =>
        `${errorCount} de ${totalRows} filas no se pueden importar porque no traen nombre de cliente. Revise que la columna correcta este asignada al dato "Nombre"; si el archivo realmente no trae esos nombres, complete la columna antes de importar.`,
    doneHelpText:
        "El listado de clientes se alimenta del buscador, que se actualiza unos segundos despues de la carga.",

    fields: CLIENT_IMPORT_FIELDS,
    requiredFields: REQUIRED_IMPORT_FIELDS,
    findField: findClientImportField,
    suggestMapping: suggestClientColumnMapping,

    loadContext: loadExistingClientData,

    resolveRows: ({ dataRows, mapping, context, policy }) =>
        resolveImportRows({
            dataRows,
            mapping: mapping as ClientColumnMapping,
            existingClients: context.clients,
            existingZones: context.zones,
            policy,
        }),

    missingLookups: (result) => {
        const names = (result as ReturnType<typeof resolveImportRows>).missingZoneNames;
        if (!names?.length) return null;
        return { label: `Se crearan ${names.length} zonas nuevas`, names };
    },

    previewColumns: [
        { header: "Codigo", render: (row) => row.data.codigo },
        { header: "Nombre", render: (row) => row.data.nombre },
        { header: "Nombre Comercial", render: (row) => row.data.nombre_comercial },
        { header: "Identificacion", render: (row) => row.data.identificacion },
        { header: "Tipo", render: (row) => row.data.tipo_identificacion },
    ],

    commit: async ({ resolved, context, onProgress }) => {
        // Las zonas nuevas se crean antes para poder mandar `zoneId` ya resueltos.
        const missingZoneNames = (resolved as ReturnType<typeof resolveImportRows>).missingZoneNames || [];
        const zones = await createMissingZones(missingZoneNames, context.zones);
        context.zones = zones;

        const rows = applyCreatedZones(resolved.rows, indexZonesByName(zones));
        const payload = buildImportPayload(rows);
        const skippedCount = payload.filter((row) => row.action === "skip").length;

        const summary = await importClients(payload, onProgress);

        return { summary, skippedCount };
    },
};

export default clientImportDefinition;
